<script setup>
import { ref, computed, onMounted, watch, nextTick, toRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { OnClickOutside } from '@vueuse/components';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBoxDropdown from 'dashboard/components-next/combobox/ComboBoxDropdown.vue';
import { useTaskProducts } from '../composables/useTaskProducts';

const props = defineProps({
  boardId: {
    type: Number,
    required: true,
  },
  currency: {
    type: String,
    default: 'USD',
  },
  initialLineItems: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:lineItems']);

const { t } = useI18n();

const {
  catalogProducts,
  fetchCatalog,
  formatCurrency,
  productDropdownOptions,
  calculateTotal,
  priceStep,
} = useTaskProducts(toRef(props, 'boardId'), toRef(props, 'currency'));

const lineItems = ref([]);
const showAddDropdown = ref(false);
const productSearchQuery = ref('');
const productDropdownRef = ref(null);

const totalValue = computed(() => calculateTotal(lineItems.value));

const emitUpdate = () => {
  emit('update:lineItems', lineItems.value);
};

const initLineItems = items => {
  lineItems.value = (items || []).map(item => ({ ...item }));
};

const addProduct = option => {
  showAddDropdown.value = false;
  productSearchQuery.value = '';
  const product = catalogProducts.value.find(p => p.id === option.value);
  if (!product) return;
  lineItems.value.push({
    tempId: Date.now(),
    product_id: product.id,
    product: { id: product.id, name: product.name },
    quantity: 1,
    unit_price: product.unit_price,
    discount_percentage: 0,
    line_total: product.unit_price,
  });
  emitUpdate();
};

const updateLineItem = (item, field, value) => {
  const index = lineItems.value.findIndex(li => {
    if (item.id != null) return li.id === item.id;
    return li.tempId === item.tempId;
  });
  if (index === -1) return;
  lineItems.value[index] = { ...lineItems.value[index], [field]: value };
  const li = lineItems.value[index];
  const qty = parseFloat(li.quantity) || 0;
  const price = parseFloat(li.unit_price) || 0;
  const discount = parseFloat(li.discount_percentage) || 0;
  lineItems.value[index].line_total = qty * price * (1 - discount / 100);
  emitUpdate();
};

const removeLineItem = item => {
  lineItems.value = lineItems.value.filter(
    li => li.id !== item.id || (!item.id && li.tempId !== item.tempId)
  );
  emitUpdate();
};

const toggleAddDropdown = () => {
  showAddDropdown.value = !showAddDropdown.value;
  if (showAddDropdown.value) {
    productSearchQuery.value = '';
    fetchCatalog();
    nextTick(() => productDropdownRef.value?.focus());
  }
};

onMounted(() => {
  initLineItems(props.initialLineItems);
  fetchCatalog();
});

watch(
  () => props.initialLineItems,
  newItems => initLineItems(newItems)
);
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex items-center justify-between">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('KANBAN.PRODUCTS.SECTION_TITLE') }}
      </span>
    </div>

    <!-- Line Items List -->
    <div class="flex flex-col gap-1">
      <!--
        Below `sm` the 12-column table has no room for three numeric inputs, so
        the row reflows to a 6-column grid: name and remove on the first line,
        the inputs (each with its own label, since the shared header is hidden)
        on the second, and the line total on its own. `order` keeps the remove
        button next to the name on mobile and at the end of the row on desktop.
      -->
      <div
        v-for="item in lineItems"
        :key="item.id || item.tempId"
        class="grid grid-cols-6 gap-2 items-center py-2 px-2 rounded-md bg-n-alpha-1 border border-n-slate-3 sm:grid-cols-12 sm:py-1.5"
      >
        <div
          class="order-1 col-span-5 text-sm text-n-slate-12 truncate sm:col-span-4"
        >
          {{ item.product?.name || t('KANBAN.PRODUCTS.UNKNOWN_PRODUCT') }}
        </div>
        <div class="order-2 col-span-1 flex justify-end sm:order-12">
          <Button
            variant="ghost"
            size="xs"
            icon="i-lucide-x"
            class="text-n-slate-10 hover:text-n-ruby-11 h-8 w-8 sm:h-5 sm:w-5"
            @click="removeLineItem(item)"
          />
        </div>
        <div class="order-3 col-span-2">
          <span
            class="block mb-1 text-[10px] text-n-slate-10 uppercase tracking-wider sm:hidden"
          >
            {{ t('KANBAN.PRODUCTS.LINE_ITEM.QTY') }}
          </span>
          <Input
            :model-value="String(parseFloat(item.quantity) || 1)"
            type="number"
            min="1"
            :aria-label="t('KANBAN.PRODUCTS.LINE_ITEM.QTY')"
            class="text-xs [&_input]:!py-1 [&_input]:!px-2"
            @change="updateLineItem(item, 'quantity', $event.target.value)"
          />
        </div>
        <div class="order-4 col-span-2">
          <span
            class="block mb-1 text-[10px] text-n-slate-10 uppercase tracking-wider sm:hidden"
          >
            {{ t('KANBAN.PRODUCTS.LINE_ITEM.PRICE') }}
          </span>
          <Input
            :model-value="String(parseFloat(item.unit_price) || 0)"
            type="number"
            min="0"
            :step="priceStep"
            :aria-label="t('KANBAN.PRODUCTS.LINE_ITEM.PRICE')"
            class="text-xs [&_input]:!py-1 [&_input]:!px-2"
            @change="updateLineItem(item, 'unit_price', $event.target.value)"
          />
        </div>
        <div class="order-5 col-span-2">
          <span
            class="block mb-1 text-[10px] text-n-slate-10 uppercase tracking-wider sm:hidden"
          >
            {{ t('KANBAN.PRODUCTS.LINE_ITEM.DISCOUNT') }}
          </span>
          <Input
            :model-value="String(parseFloat(item.discount_percentage) || 0)"
            type="number"
            min="0"
            max="100"
            step="0.1"
            :aria-label="t('KANBAN.PRODUCTS.LINE_ITEM.DISCOUNT')"
            class="text-xs [&_input]:!py-1 [&_input]:!px-2"
            @change="
              updateLineItem(item, 'discount_percentage', $event.target.value)
            "
          />
        </div>
        <div
          class="order-6 col-span-6 flex items-center justify-between text-xs text-n-slate-11 font-medium sm:col-span-1 sm:justify-end"
        >
          <span
            class="uppercase tracking-wider text-[10px] text-n-slate-10 sm:hidden"
          >
            {{ t('KANBAN.PRODUCTS.LINE_ITEM.TOTAL') }}
          </span>
          <span>{{ formatCurrency(item.line_total) }}</span>
        </div>
      </div>

      <!-- Column labels -->
      <div
        v-if="lineItems.length"
        class="hidden grid-cols-12 gap-2 px-2 text-[10px] text-n-slate-10 uppercase tracking-wider mt-0.5 sm:grid"
      >
        <div class="col-span-4">
          {{ t('KANBAN.PRODUCTS.TABLE.NAME') }}
        </div>
        <div class="col-span-2">
          {{ t('KANBAN.PRODUCTS.LINE_ITEM.QTY') }}
        </div>
        <div class="col-span-2">
          {{ t('KANBAN.PRODUCTS.LINE_ITEM.PRICE') }}
        </div>
        <div class="col-span-2">
          {{ t('KANBAN.PRODUCTS.LINE_ITEM.DISCOUNT') }}
        </div>
        <div class="col-span-1 text-right">
          {{ t('KANBAN.PRODUCTS.LINE_ITEM.TOTAL') }}
        </div>
        <div class="col-span-1" />
      </div>
    </div>

    <!-- Add Product -->
    <OnClickOutside @trigger="showAddDropdown = false">
      <div class="relative">
        <Button
          variant="ghost"
          color="slate"
          size="sm"
          icon="i-lucide-plus"
          class="w-full justify-start text-n-slate-11 hover:text-n-slate-12"
          @click="toggleAddDropdown"
        >
          {{ t('KANBAN.PRODUCTS.ADD_LINE_ITEM') }}
        </Button>
        <ComboBoxDropdown
          ref="productDropdownRef"
          :open="showAddDropdown"
          :options="productDropdownOptions"
          :search-value="productSearchQuery"
          :search-placeholder="t('KANBAN.PRODUCTS.SEARCH_CATALOG')"
          :empty-state="t('KANBAN.PRODUCTS.NO_PRODUCTS_AVAILABLE')"
          @update:search-value="productSearchQuery = $event"
          @select="addProduct"
        />
      </div>
    </OnClickOutside>

    <!-- Total -->
    <div
      v-if="lineItems.length"
      class="flex items-center justify-between pt-2 border-t border-n-slate-3"
    >
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('KANBAN.PRODUCTS.TOTAL') }}
      </span>
      <span class="text-sm font-semibold text-n-slate-12">
        {{ formatCurrency(totalValue) }}
      </span>
    </div>
  </div>
</template>
